import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { RbacService } from '../../../../shared/rbac.service';
import { EnvironmentService } from '../../../../shared/environment.service';
import { FirebaseAuthService } from '../../../../shared/firebase-auth.service';
import { Router } from '@angular/router';
import { Observable, lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { User } from 'firebase/auth';
import { AccountMenuComponent } from '../../ui/account-menu/account-menu.component';
import {
  ColumnFilterComponent,
  TableFiltersDialogComponent,
  ColumnFilterConfig,
  ColumnFilterCriterion,
  TableFilterState,
  TextFilterCriterion,
  CategoricalFilterCriterion,
  DateRangeFilterCriterion,
  NumberRangeFilterCriterion,
  getActiveFilterCount,
  isCriterionActive,
  matchesTextCriterion,
  matchesCategoricalCriterion,
  matchesDateRangeCriterion,
  matchesNumberRangeCriterion,
} from '../../../../shared/table-filter';

/** Compact dialog is only used on desktop when the user has this many projects or fewer. */
export const PROJECT_SELECTOR_COMPACT_MAX = 6;

/** Viewports at or below this width always use the full-screen selector. */
export const PROJECT_SELECTOR_HANDSET_QUERY = '(max-width: 768px)';

export interface ProjectSelectionDialogData {
  projects?: SelectableProject[];
  showCancel?: boolean;
}

/** Row shown in the project selector. Mirrors GetUserProjects `ProjectPermission.toObject()`. */
export interface SelectableProject {
  architecturalProjectId: string;
  projectName?: string;
  role?: number;
  formattedAddress?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  /** Pages successfully ingested (page.md generated). Issue #1682. */
  ingestedPagesCount?: number;
  /** Pages with code applicability analysis. Issue #1682. */
  analyzedPagesCount?: number;
  /** Evaluated ICC building code sections. Issue #1682. */
  complianceSectionsCount?: number;
  /** Total pages across uploaded PDFs, extracted from per-file metadata. Issue #1711. */
  uploadedPagesCount?: number;
  /** False when the backend could not verify every uploaded file's page count. */
  hasUploadedPagesCount?: boolean;
  /**
   * True when the ingestion, analysis, and compliance counters were computed. False means the
   * project has never been processed, which is a different statement from "processed, found
   * nothing". Issue #1682.
   */
  hasProgressData?: boolean;
}

/**
 * True when the selector should fill the viewport instead of a compact dialog.
 *
 * @param expanded the user's explicit request to expand, which overrides the project-count
 *     heuristic but never the handset rule - see {@link canOfferFullscreenToggle}
 */
export function shouldUseFullscreen(
  projectCount: number,
  isHandset: boolean,
  compactMax = PROJECT_SELECTOR_COMPACT_MAX,
  expanded = false
): boolean {
  return isHandset || projectCount > compactMax || expanded;
}

/**
 * True when a manual expand control would actually deliver the sortable table.
 *
 * <p>The project-count rule is a guess about intent, and it can guess wrong - someone with three
 * projects may still want to compare them. So the guess gets a manual override. But the override
 * is only offered where it can be honoured:
 *
 * <ul>
 *   <li>At or below the handset threshold the table never renders at any project count, so an
 *       expand control there would promise a layout that cannot be delivered.
 *   <li>Above the project-count threshold the table is already showing, so the control would do
 *       nothing.
 *   <li>With no projects there is nothing to put in a table.
 * </ul>
 *
 * <p>The threshold comes from {@link PROJECT_SELECTOR_HANDSET_QUERY} via the same breakpoint
 * observation that drives the automatic layout - deliberately not a second constant, because two
 * constants for one concept is how the responsive column rules ended up inert.
 */
export function canOfferFullscreenToggle(
  projectCount: number,
  isHandset: boolean,
  compactMax = PROJECT_SELECTOR_COMPACT_MAX
): boolean {
  return !isHandset && projectCount > 0 && projectCount <= compactMax;
}

/** True when the full-viewport desktop layout should be the sortable table. */
export function shouldUseTableLayout(isFullscreen: boolean, isHandset: boolean): boolean {
  return isFullscreen && !isHandset;
}

/**
 * Progress columns and the `SelectableProject` field each one ranks on (Issue #1682). These sort
 * on the presence flag first and the count second, so an unprocessed project never ties with a
 * project that was processed and genuinely found nothing.
 */
const PROGRESS_SORT_KEYS: Readonly<Record<string, keyof SelectableProject>> = {
  ingested: 'ingestedPagesCount',
  analyzed: 'analyzedPagesCount',
  compliance: 'complianceSectionsCount',
};

/** Searchable project picker: compact list for a handful of projects, full-screen table otherwise (Issue #1581). */
@Component({
  selector: 'app-project-selection-dialog',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    ColumnFilterComponent,
    AccountMenuComponent,
  ],
  templateUrl: './project-selection-dialog.component.html',
  styleUrls: ['./project-selection-dialog.component.scss'],
})
export class ProjectSelectionDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ProjectSelectionDialogComponent>>(MatDialogRef);
  data = inject<ProjectSelectionDialogData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private rbacService = inject(RbacService);
  private environmentService = inject(EnvironmentService);
  private firebaseAuthService = inject(FirebaseAuthService);
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  user$: Observable<User | null> = this.firebaseAuthService.user$;

  displayedColumns = ['name', 'address', 'ingested', 'analyzed', 'compliance', 'created', 'modified', 'creator'];
  dataSource = new MatTableDataSource<SelectableProject>([]);
  searchControl = new FormControl('', { nonNullable: true });

  filterState: TableFilterState = {};
  columnFilterConfigs: Record<string, ColumnFilterConfig> = {};

  projects: SelectableProject[] = [];
  creatorOptions: string[] = [];
  loading = true;
  error: string | null = null;
  canCreateNewProject = true;
  newProjectTooltip = 'Create a new project';
  isHandset = false;
  isFullscreen = false;
  /** The user's explicit request to expand into the table, overriding the project-count guess. */
  expanded = false;

  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    this.dataSource.sort = sort ?? null;
  }

  /**
   * Opens the selector with config that can grow from a compact dialog to a full-viewport table.
   * `showCancel: false` (landing page) also disables backdrop/escape close.
   */
  static open(dialog: MatDialog, data: ProjectSelectionDialogData = {}): MatDialogRef<ProjectSelectionDialogComponent> {
    const showCancel = data.showCancel === true;
    return dialog.open(ProjectSelectionDialogComponent, {
      width: '640px',
      maxWidth: '100vw',
      autoFocus: 'first-tabbable',
      panelClass: ['project-selector-dialog'],
      data,
      disableClose: !showCancel,
    });
  }

  constructor() {
    this.dataSource.filterPredicate = (project, filterJson) => this.matchesFilter(project, filterJson);
    this.dataSource.sortingDataAccessor = (project, column) => this.sortValue(project, column);

    // Progress columns need null-last ordering, which a plain comparator cannot express: an
    // unprocessed project must pin to the bottom in BOTH directions rather than float to the top
    // of an ascending sort. Everything else keeps Material's default sorting.
    const defaultSortData = this.dataSource.sortData;
    this.dataSource.sortData = (data: SelectableProject[], sort: MatSort): SelectableProject[] =>
      PROGRESS_SORT_KEYS[sort.active] && sort.direction
        ? this.sortByProgress(data, PROGRESS_SORT_KEYS[sort.active], sort.direction)
        : defaultSortData(data, sort);

    this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applyFilter());
  }

  ngOnInit(): void {
    this.updateColumnFilterConfigs();
    this.breakpointObserver
      .observe(PROJECT_SELECTOR_HANDSET_QUERY)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.isHandset = state.matches;
        this.applyLayout();
      });

    let initialUserSeen = false;
    let lastUserId: string | null = null;
    this.firebaseAuthService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async user => {
      const currentUserId = user ? user.uid : null;
      if (!initialUserSeen) {
        initialUserSeen = true;
        lastUserId = currentUserId;
        return;
      }

      if (lastUserId !== currentUserId) {
        ClientLoggingService.log(
          'INFO',
          `[ProjectSelectionDialog] User changed from ${lastUserId} to ${currentUserId}, reloading projects`
        );
        lastUserId = currentUserId;
        this.clearAccountData();
        if (user) {
          await this.initialize();
        } else {
          this.loading = false;
        }
      }
    });

    void this.initialize();
  }

  get showTable(): boolean {
    return shouldUseTableLayout(this.isFullscreen, this.isHandset);
  }

  /** True when the expand/collapse control can deliver the layout it offers. */
  get canToggleFullscreen(): boolean {
    return canOfferFullscreenToggle(this.projects.length, this.isHandset);
  }

  /**
   * Label and accessible name for the expand/collapse control.
   *
   * <p>Both labels describe what the click will DO, so the control is an action button rather than
   * a toggle button. The expand label names SORTING rather than the counts: the compact card
   * already shows all three counts and the same metadata, so promising the columns would promise
   * something the user already has. What the table adds is alignment and sortability. It deliberately carries no `aria-pressed`: a pressed state and a label that
   * changes with state each report the control's status, and here they would report it in opposite
   * directions - in the table a screen reader would announce "Compact view, pressed", stating that
   * compact view is the active state when it is precisely the state that is off.
   */
  get fullscreenToggleLabel(): string {
    return this.expanded ? 'Compact view' : 'Sort and compare';
  }

  /**
   * Hover and focus text for the expand/collapse control.
   *
   * <p>Two properties are doing work here and both are easy to lose in an edit.
   *
   * <p><b>It begins with the accessible name, exactly.</b> The button shows no visible text, so
   * `aria-label` is the only name it has, and speech-control users say what they can see. A hint
   * that diverged from the name would leave the control unaddressable by voice - a harder failure
   * than an unhelpful tooltip. Do not paraphrase the opening words.
   *
   * <p><b>It is longer than the name on purpose.</b> For a sighted user the meaning is split
   * across two channels: the icon carries "this changes the layout" and the text carries "sort and
   * compare". A screen reader user gets only the text, so a terse hint does not deliver the same
   * message more quietly - it delivers a smaller one, missing exactly the part the icon was
   * carrying. `matTooltip` sets `aria-describedby`, so this is announced in addition to the name;
   * a hint identical to the name would spend that slot saying the name twice. Were screen readers
   * out of scope, terse would be right.
   */
  get fullscreenToggleHint(): string {
    return this.expanded
      ? 'Compact view \u2013 returns to the compact list'
      : 'Sort and compare \u2013 expands to a full-width table';
  }

  /** Expands into the sortable table, or returns to the compact list. */
  toggleFullscreen(): void {
    this.expanded = !this.expanded;
    this.applyLayout();
  }

  get visibleProjects(): SelectableProject[] {
    return this.dataSource.filteredData;
  }

  get hasActiveFilter(): boolean {
    return this.searchControl.value.trim().length > 0 || this.activeColumnFilterCount > 0;
  }

  get activeColumnFilterCount(): number {
    return getActiveFilterCount(this.filterState);
  }

  get noMatchesText(): string {
    if (this.activeColumnFilterCount > 0 && this.searchControl.value.trim().length > 0) {
      return 'No projects match this search and active filters.';
    }
    if (this.activeColumnFilterCount > 0) {
      return 'No projects match the active filters.';
    }
    return 'No projects match this search.';
  }

  get noMatchesHint(): string {
    if (this.activeColumnFilterCount > 0 && this.searchControl.value.trim().length > 0) {
      return 'Try adjusting your search terms or clearing column filters.';
    }
    if (this.activeColumnFilterCount > 0) {
      return 'Try adjusting or clearing your column filters.';
    }
    return 'Try a different name, address, or creator.';
  }

  updateColumnFilterConfigs(): void {
    this.columnFilterConfigs = {
      name: {
        columnId: 'name',
        columnLabel: 'Name / ID',
        type: 'text',
        placeholder: 'Filter by name or ID...',
      },
      address: {
        columnId: 'address',
        columnLabel: 'Address',
        type: 'text',
        placeholder: 'Filter by address...',
      },
      ingested: {
        columnId: 'ingested',
        columnLabel: 'Ingested pages',
        type: 'number-range',
        targetOptions: [
          { value: 'count', label: 'Ingested pages count' },
          { value: 'denominator', label: 'Uploaded total pages' },
        ],
        defaultTarget: 'count',
      },
      analyzed: {
        columnId: 'analyzed',
        columnLabel: 'Analyzed pages',
        type: 'number-range',
        targetOptions: [
          { value: 'count', label: 'Analyzed pages count' },
          { value: 'denominator', label: 'Ingested total pages' },
        ],
        defaultTarget: 'count',
      },
      compliance: {
        columnId: 'compliance',
        columnLabel: 'Compliance sections',
        type: 'number-range',
        targetOptions: [{ value: 'count', label: 'Compliance sections count' }],
        defaultTarget: 'count',
      },
      created: {
        columnId: 'created',
        columnLabel: 'Created date',
        type: 'date-range',
      },
      modified: {
        columnId: 'modified',
        columnLabel: 'Last modified date',
        type: 'date-range',
      },
      creator: {
        columnId: 'creator',
        columnLabel: 'Created by',
        type: 'categorical',
        options: this.creatorOptions,
      },
    };
  }

  get columnFilterConfigsList(): ColumnFilterConfig[] {
    return this.displayedColumns.map(col => this.columnFilterConfigs[col]).filter(Boolean);
  }

  onColumnFilterChange(columnId: string, criterion?: ColumnFilterCriterion): void {
    if (criterion && isCriterionActive(criterion)) {
      this.filterState = { ...this.filterState, [columnId]: criterion };
    } else {
      const next = { ...this.filterState };
      delete next[columnId];
      this.filterState = next;
    }

    this.applyFilter();
  }

  clearAllColumnFilters(): void {
    this.filterState = {};
    this.applyFilter();
  }

  openFiltersDialog(): void {
    const dialogRef = this.dialog.open(TableFiltersDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      panelClass: 'table-filters-dialog-panel',
      data: {
        configs: this.columnFilterConfigsList,
        state: { ...this.filterState },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: TableFilterState) => {
        if (result) {
          this.filterState = result;
          this.applyFilter();
        }
      });
  }

  displayName(project: SelectableProject): string {
    return project.projectName?.trim() || project.architecturalProjectId;
  }

  lastModified(project: SelectableProject): string {
    return project.updatedAt?.trim() || project.createdAt?.trim() || '';
  }

  /**
   * Normalizes a progress counter for display and numeric sorting (Issue #1682).
   * Missing, null, or non-finite values from legacy projects render as `0`
   * instead of blank, `null`, or `NaN`.
   */
  countValue(value: number | undefined | null): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
      return 0;
    }
    return Math.trunc(value);
  }

  /** True when the project has any recorded ingestion or analysis progress. */
  hasProgress(project: SelectableProject): boolean {
    return (
      this.countValue(project.ingestedPagesCount) > 0 ||
      this.countValue(project.analyzedPagesCount) > 0 ||
      this.countValue(project.complianceSectionsCount) > 0
    );
  }

  /**
   * True when the backend actually computed this project's counters.
   *
   * <p>A `0` from a processed project means "we looked and found nothing"; a `0` from an
   * unprocessed project means "we have not looked yet". The backend sends `hasProgressData` so the
   * two can be told apart; older responses without the field fall back to inferring it from the
   * counts themselves.
   */
  isProcessed(project: SelectableProject): boolean {
    return project.hasProgressData === true || this.hasProgress(project);
  }

  /**
   * Text for a project whose counters are not known.
   *
   * <p>The wording deliberately asserts nothing about cause. This state covers two situations with
   * genuinely different truths - the project has no artifacts at all, and the scan of its artifacts
   * could not be completed - so "no analysis data" would be wrong about the second and "could not
   * read analysis data" would be wrong about the first. "Unavailable" is honest under both.
   */
  progressHint(project: SelectableProject): string {
    return this.isProcessed(project) ? '' : 'Analysis data unavailable';
  }

  /**
   * Text for one progress cell: the number when the project was processed - a genuine `0`
   * included, meaning "we looked and found nothing" - and an em dash when it was never processed.
   */
  countDisplay(project: SelectableProject, value: number | undefined): string {
    return this.isProcessed(project) ? String(this.countValue(value)) : '\u2014';
  }

  /**
   * Accessible name for one progress cell. An em dash needs a label more than a numeral does, so
   * unprocessed cells announce what they are rather than reading out a bare glyph.
   */
  countLabel(project: SelectableProject, value: number | undefined, label: string): string {
    const count = this.countValue(value);
    const unit = count === 1 ? label.toLowerCase().replace(/s$/, '') : label.toLowerCase();
    return this.isProcessed(project) ? `${count} ${unit}` : `${label}: analysis data unavailable`;
  }

  /** Visible denominator for the Ingested column. */
  uploadedPagesDisplay(project: SelectableProject): string {
    return project.hasUploadedPagesCount === true ? String(this.countValue(project.uploadedPagesCount)) : 'unknown';
  }

  /** Accessible description of ingestion progress and its independently verified denominator. */
  ingestedProgressLabel(project: SelectableProject): string {
    if (!this.isProcessed(project)) {
      return 'Ingested pages: analysis data unavailable';
    }

    const ingested = this.countValue(project.ingestedPagesCount);
    if (project.hasUploadedPagesCount !== true) {
      return `Ingested pages: ${ingested}; uploaded page total unavailable`;
    }

    const uploaded = this.countValue(project.uploadedPagesCount);
    return `Ingested pages: ${ingested} of ${uploaded} ${uploaded === 1 ? 'page' : 'pages'}`;
  }

  /** Accessible description of analyzed pages measured against successfully ingested pages. */
  analyzedProgressLabel(project: SelectableProject): string {
    const ingested = this.countValue(project.ingestedPagesCount);
    return this.isProcessed(project)
      ? `Analyzed pages: ${this.countValue(project.analyzedPagesCount)} of ${ingested} ingested ${ingested === 1 ? 'page' : 'pages'}`
      : 'Analyzed pages: analysis data unavailable';
  }

  /**
   * Compact one-line progress summary for the mobile / small-list card layout,
   * e.g. `Ingested 132 of 200 pages · Analyzed 43 of 132 pages · Compliance 333 sections`.
   */
  progressLine(project: SelectableProject): string {
    if (!this.isProcessed(project)) {
      return 'Analysis data unavailable';
    }

    const ingested = this.countValue(project.ingestedPagesCount);
    const uploaded = this.countValue(project.uploadedPagesCount);
    const analyzed = this.countValue(project.analyzedPagesCount);
    const compliance = this.countValue(project.complianceSectionsCount);
    const ingestedUnit = ingested === 1 ? 'page' : 'pages';
    const uploadedUnit = uploaded === 1 ? 'page' : 'pages';

    const ingestion =
      project.hasUploadedPagesCount === true
        ? `Ingested ${ingested} of ${uploaded} ${uploadedUnit}`
        : `Ingested ${ingested} ${ingestedUnit} (total unavailable)`;

    return [
      ingestion,
      `Analyzed ${analyzed} of ${ingested} ${ingestedUnit}`,
      `Compliance ${compliance} ${compliance === 1 ? 'section' : 'sections'}`,
    ].join(' \u00b7 ');
  }

  selectProject(project: SelectableProject): void {
    this.dialogRef.close(project);
  }

  onRowKeydown(event: KeyboardEvent, project: SelectableProject): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectProject(project);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onNewProject(): void {
    this.dialogRef.close({ action: 'new-project' });
  }

  clearAccountData(): void {
    this.data.projects = undefined;
    this.projects = [];
    this.dataSource.data = [];
    this.creatorOptions = [];
    this.filterState = {};
    this.searchControl.setValue('', { emitEvent: false });
    this.applyFilter();
    this.applyLayout();
  }

  async onSwitchAccount(): Promise<void> {
    this.clearAccountData();
    this.loading = true;
    this.error = null;
    try {
      await this.firebaseAuthService.signIn(true);
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSelectionDialog] Error during account switch:', error);
      this.loading = false;
      this.error = 'Failed to switch account. Please try again.';
    }
  }

  onNavigateToBilling(): void {
    ClientLoggingService.log('INFO', '[ProjectSelectionDialog] Navigating to billing page');
    this.dialogRef.close({ action: 'billing' });
    void this.router.navigate(['/billing']);
  }

  onNavigateToAdmin(): void {
    ClientLoggingService.log('INFO', '[ProjectSelectionDialog] Navigating to admin console');
    this.dialogRef.close({ action: 'admin' });
    void this.router.navigate(['/admin']);
  }

  async onSignOut(): Promise<void> {
    ClientLoggingService.log('INFO', '[ProjectSelectionDialog] Signing out');
    this.dialogRef.close({ action: 'sign-out' });
    try {
      await this.firebaseAuthService.signOut();
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSelectionDialog] Error signing out:', error);
    }
  }

  retryLoad(): void {
    void this.loadProjects();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  private async initialize(): Promise<void> {
    await this.checkNewProjectPermissions();

    if (this.data.projects && this.data.projects.length > 0) {
      this.setProjects(this.data.projects);
      this.loading = false;
      return;
    }

    await this.loadProjects();
  }

  private async loadProjects(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const raw = await this.rbacService.getUserProjects();
      const projects = raw.map(project => this.toSelectableProject(project));
      ClientLoggingService.log('INFO', `[ProjectSelectionDialog] Loaded ${projects.length} projects`, projects);
      this.setProjects(projects);

      if (projects.length === 0) {
        ClientLoggingService.log('INFO', '[ProjectSelectionDialog] No projects found - showing empty state');
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSelectionDialog] Error loading projects:', error);

      if (this.environmentService.isDevelopment()) {
        this.error =
          'Development Mode: The mock user (dev@example.com) does not have permissions to access projects. ' +
          'This is expected in local development. In production, real authenticated users will have proper permissions.';
      } else {
        this.error = 'Failed to load projects. Please try again.';
      }
    } finally {
      this.loading = false;
      this.applyLayout();
    }
  }

  private setProjects(projects: SelectableProject[]): void {
    this.projects = [...projects].sort(
      (a, b) => this.timestampValue(this.lastModified(b)) - this.timestampValue(this.lastModified(a))
    );
    this.creatorOptions = [
      ...new Set(this.projects.map(project => project.createdBy?.trim()).filter((value): value is string => !!value)),
    ].sort((a, b) => a.localeCompare(b));
    this.updateColumnFilterConfigs();
    this.dataSource.data = this.projects;
    this.applyFilter();
    this.applyLayout();
  }

  private applyFilter(): void {
    const filter = {
      search: this.searchControl.value.trim().toLowerCase(),
      columns: this.filterState,
    };
    this.dataSource.filter = JSON.stringify(filter);
  }

  private matchesFilter(project: SelectableProject, filterJson: string): boolean {
    let filterData: { search: string; columns: TableFilterState } = { search: '', columns: {} };
    try {
      filterData = JSON.parse(filterJson || '{}');
    } catch {
      filterData = { search: '', columns: {} };
    }

    const columns = filterData.columns || {};

    // 1. Column filters (Logical AND across columns)
    if (columns['name'] && columns['name'].type === 'text') {
      const textCrit = columns['name'] as TextFilterCriterion;
      const matchesName =
        matchesTextCriterion(this.displayName(project), textCrit) ||
        matchesTextCriterion(project.architecturalProjectId, textCrit);
      if (!matchesName) return false;
    }

    if (columns['address'] && columns['address'].type === 'text') {
      if (!matchesTextCriterion(project.formattedAddress, columns['address'] as TextFilterCriterion)) {
        return false;
      }
    }

    if (columns['ingested'] && columns['ingested'].type === 'number-range') {
      const numCrit = columns['ingested'] as NumberRangeFilterCriterion;
      const target = numCrit.target || 'count';
      let value: number | null | undefined;
      let isKnown = false;
      if (target === 'denominator') {
        value = project.uploadedPagesCount;
        isKnown = Boolean(project.hasUploadedPagesCount && project.uploadedPagesCount !== undefined);
      } else {
        value = project.ingestedPagesCount;
        isKnown = Boolean(this.isProcessed(project) && project.ingestedPagesCount !== undefined);
      }
      if (!matchesNumberRangeCriterion(value, numCrit, isKnown)) {
        return false;
      }
    }

    if (columns['analyzed'] && columns['analyzed'].type === 'number-range') {
      const numCrit = columns['analyzed'] as NumberRangeFilterCriterion;
      const target = numCrit.target || 'count';
      let value: number | null | undefined;
      let isKnown = false;
      if (target === 'denominator') {
        value = project.ingestedPagesCount;
        isKnown = Boolean(this.isProcessed(project) && project.ingestedPagesCount !== undefined);
      } else {
        value = project.analyzedPagesCount;
        isKnown = Boolean(this.isProcessed(project) && project.analyzedPagesCount !== undefined);
      }
      if (!matchesNumberRangeCriterion(value, numCrit, isKnown)) {
        return false;
      }
    }

    if (columns['compliance'] && columns['compliance'].type === 'number-range') {
      const numCrit = columns['compliance'] as NumberRangeFilterCriterion;
      const value = project.complianceSectionsCount;
      const isKnown = Boolean(this.isProcessed(project) && project.complianceSectionsCount !== undefined);
      if (!matchesNumberRangeCriterion(value, numCrit, isKnown)) {
        return false;
      }
    }

    if (columns['created'] && columns['created'].type === 'date-range') {
      if (!matchesDateRangeCriterion(project.createdAt, columns['created'] as DateRangeFilterCriterion)) {
        return false;
      }
    }

    if (columns['modified'] && columns['modified'].type === 'date-range') {
      if (!matchesDateRangeCriterion(this.lastModified(project), columns['modified'] as DateRangeFilterCriterion)) {
        return false;
      }
    }

    if (columns['creator'] && columns['creator'].type === 'categorical') {
      if (!matchesCategoricalCriterion(project.createdBy, columns['creator'] as CategoricalFilterCriterion)) {
        return false;
      }
    }

    // 2. Global search
    if (filterData.search) {
      const haystack = [
        this.displayName(project),
        project.architecturalProjectId,
        project.formattedAddress,
        project.createdBy,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!haystack.includes(filterData.search)) {
        return false;
      }
    }

    return true;
  }

  private sortValue(project: SelectableProject, column: string): string | number {
    switch (column) {
      case 'name':
        return this.displayName(project).toLowerCase();
      case 'address':
        return (project.formattedAddress || '').toLowerCase();
      case 'ingested':
        return this.countValue(project.ingestedPagesCount);
      case 'analyzed':
        return this.countValue(project.analyzedPagesCount);
      case 'compliance':
        return this.countValue(project.complianceSectionsCount);
      case 'created':
        return this.timestampValue(project.createdAt);
      case 'modified':
        return this.timestampValue(this.lastModified(project));
      case 'creator':
        return (project.createdBy || '').toLowerCase();
      default:
        return '';
    }
  }

  /**
   * Orders rows by one progress column, keeping unprocessed projects last in both directions.
   *
   * <p>The column answers "which revision is furthest along", so a project nobody has processed is
   * not an answer at either end of the ranking — it is the absence of one. Sorting on the presence
   * flag first also stops an unprocessed row from tying with a project that really was processed
   * and found nothing.
   */
  private sortByProgress(
    data: SelectableProject[],
    key: keyof SelectableProject,
    direction: 'asc' | 'desc'
  ): SelectableProject[] {
    const factor = direction === 'asc' ? 1 : -1;

    return [...data].sort((left, right) => {
      const leftProcessed = this.isProcessed(left);
      const rightProcessed = this.isProcessed(right);

      if (leftProcessed !== rightProcessed) {
        return leftProcessed ? -1 : 1;
      }
      if (!leftProcessed) {
        return 0;
      }

      return (
        (this.countValue(left[key] as number | undefined) - this.countValue(right[key] as number | undefined)) * factor
      );
    });
  }

  private timestampValue(value: string | undefined): number {
    if (!value?.trim()) {
      return 0;
    }
    const millis = Date.parse(value);
    return Number.isNaN(millis) ? 0 : millis;
  }

  private applyLayout(): void {
    const fullscreen = shouldUseFullscreen(
      this.projects.length,
      this.isHandset,
      PROJECT_SELECTOR_COMPACT_MAX,
      this.expanded
    );
    this.isFullscreen = fullscreen;

    if (fullscreen) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('project-selector-fullscreen');
      this.dialogRef.removePanelClass('project-selector-compact');
    } else {
      this.dialogRef.updateSize('640px');
      this.dialogRef.addPanelClass('project-selector-compact');
      this.dialogRef.removePanelClass('project-selector-fullscreen');
    }
  }

  private toSelectableProject(raw: Record<string, unknown> | SelectableProject | null | undefined): SelectableProject {
    if (!raw) {
      return {
        architecturalProjectId: '',
        projectName: '',
        formattedAddress: '',
        createdAt: '',
        updatedAt: '',
        createdBy: '',
        ingestedPagesCount: 0,
        analyzedPagesCount: 0,
        complianceSectionsCount: 0,
        uploadedPagesCount: 0,
        hasUploadedPagesCount: false,
        hasProgressData: false,
      };
    }

    return {
      architecturalProjectId: String(raw.architecturalProjectId || ''),
      projectName: typeof raw.projectName === 'string' ? raw.projectName : '',
      role: typeof raw.role === 'number' ? raw.role : undefined,
      formattedAddress: typeof raw.formattedAddress === 'string' ? raw.formattedAddress : '',
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
      updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
      createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : '',
      ingestedPagesCount: this.countValue(raw.ingestedPagesCount as number | undefined),
      analyzedPagesCount: this.countValue(raw.analyzedPagesCount as number | undefined),
      complianceSectionsCount: this.countValue(raw.complianceSectionsCount as number | undefined),
      uploadedPagesCount: this.countValue(raw.uploadedPagesCount as number | undefined),
      hasUploadedPagesCount: raw.hasUploadedPagesCount === true,
      hasProgressData: raw.hasProgressData === true,
    };
  }

  /**
   * Check if the current user has permission to create new projects.
   * Issue #253: Check Firebase authentication status directly so users with empty
   * project lists can still create their first project.
   */
  private async checkNewProjectPermissions(): Promise<void> {
    try {
      const user = await lastValueFrom(this.firebaseAuthService.user$.pipe(take(1)));

      if (user && user.email) {
        this.canCreateNewProject = true;
        this.newProjectTooltip = 'Create a new project';
        ClientLoggingService.log(
          'INFO',
          '[ProjectSelectionDialog] User authenticated, can create projects:',
          user.email
        );
      } else {
        this.canCreateNewProject = false;
        this.newProjectTooltip = 'Authentication required to create projects';
        ClientLoggingService.log('INFO', '[ProjectSelectionDialog] No authenticated user, cannot create projects');
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSelectionDialog] Error checking authentication:', error);
      this.canCreateNewProject = false;

      if (this.environmentService.isDevelopment()) {
        this.newProjectTooltip = 'Development Mode: Authentication required to create projects';
      } else {
        this.newProjectTooltip = 'Authentication required to create projects';
      }
    }
  }
}
